/* eslint-disable max-len */
import { Component, EventEmitter, Input, OnInit, Output, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule , isPlatformBrowser } from '@angular/common';
import { SafeUrl, DomSanitizer } from '@angular/platform-browser';
import { ThemeService, ThemeColors, SharedUiLibraryModule } from '@cudirect/shared-ui-library';
import { ICheckGatewayConfig } from '@shared/model/icheck-gateway.model';
import { ICheckGatewayService } from '@shared/service/icheck-gateway/icheck-gateway.service';

@Component({
  selector: 'dx-icheck-gateway',
  templateUrl: './icheck-gateway.component.html',
  styleUrls: ['./icheck-gateway.component.scss'],
  standalone: true,
  imports: [CommonModule, SharedUiLibraryModule]
})
export class ICheckGatewayComponent implements OnInit {
  // Using inject pattern for dependencies
  private sanitizer = inject(DomSanitizer);
  private themeService = inject(ThemeService);
  private icheckGatewayService = inject(ICheckGatewayService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @Input() public icgConfig: ICheckGatewayConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() public transactionResponse: EventEmitter<any> = new EventEmitter<any>();
  @Output() public icgSelectedPaymentType: EventEmitter<string> = new EventEmitter<string>();
  public trustedUrl: SafeUrl;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public paymentDetails: any;
  public errorMessage: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public paymentTypes: any = [];
  public selectedPaymentType: string;
  public iframeHeight = '725px';
  public transactionApproved: boolean;
  public transactionSuccessful = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public transactionError: any;

  public accountTypes = new Map([
    ['PC', 'Personal Checking'],
    ['PS', 'Personal Savings'],
    ['BC', 'Business Checking'],
    ['BS', 'Business Savings'],
    ['GL', 'General Ledger']
  ]);

  constructor() {
    if (this.isBrowser) {
      if (window.addEventListener) {
        window.addEventListener('message', this.receiveMessage.bind(this), false);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).attachEvent('onmessage', this.receiveMessage.bind(this));
      }
    }
  }

  public ngOnInit() {
    // Check to see if input is a JSON string, if so, lets parse it
    if (typeof this.icgConfig === 'string') {
      this.icgConfig = JSON.parse(this.icgConfig);
    }

    if (this.icgConfig && this.icgConfig.iframeUrls && Object.keys(this.icgConfig.iframeUrls).length === 1) {
      this.selectedPayment(this.icgConfig.iframeUrls[0].key);
    }

    // Check only one payment option allowed (or) to display radio button list.
    if (this.icgConfig?.fundingTypeOptions) {
      if (this.icgConfig.fundingTypeOptions.creditDebit && this.icgConfig.fundingTypeOptions.bankTransfer) {
        this.paymentTypes = this.preparePaymentTypes(
          'Debit or credit card - funds instantly',
          'Bank Transfer (ACH) - funds within 2 to 3 days'
        );
      } else if (this.icgConfig.fundingTypeOptions.creditDebit) {
        this.selectedPayment('CC');
      } else if (this.icgConfig.fundingTypeOptions.bankTransfer) {
        this.selectedPayment('ACH');
      }
    } else {
      this.paymentTypes = this.preparePaymentTypes(
        'Debit or credit card - funds instantly',
        'Bank Transfer (ACH) - funds within 2 to 3 days'
      );
    }
  }

  public preparePaymentTypes(ccText: string, achText: string) {
    this.paymentTypes = [];
    this.icgConfig?.iframeUrls.forEach((item) => {
      const val = item.key;
      const name = val === 'CC' ? ccText : achText;
      // eslint-disable-next-line object-shorthand
      this.paymentTypes.push({ name: name, value: val });
    });
    this.iframeHeight = this.selectedPaymentType === 'CC' ? '1280px' : '640px';
    return this.paymentTypes;
  }

  public isACH(): boolean {
    return this.selectedPaymentType === 'ACH';
  }

  public print(): void {
    if (this.isBrowser) {
      // this will set print css only when the user uses print button
      document.body.classList.add('payment-gateway-print');
      window.print();
      // this will revert css
      setTimeout(() => {
        document.body.classList.remove('payment-gateway-print');
      }, 100);
    }
  }

  public iFrameUrlBuilder(): void {
    let url = `${this.getIframeUrl()}?appId=${this.icgConfig.appId}&appSecret=${this.icgConfig.appSecret}&amount=${
      this.icgConfig.paymentData.amount
    }&firstName=${this.icgConfig.paymentData.firstName}`;

    if (this.icgConfig.paymentData.lastName) {
      url = `${url}&lastName=${this.icgConfig.paymentData.lastName}`;
    }
    if (this.icgConfig.paymentData.company) {
      url = `${url}&company=${this.icgConfig.paymentData.company}`;
    }
    if (this.icgConfig.paymentData.street1) {
      url = `${url}&street1=${this.icgConfig.paymentData.street1}`;
    }
    if (this.icgConfig.paymentData.street2) {
      url = `${url}&street2=${this.icgConfig.paymentData.street2}`;
    }
    if (this.icgConfig.paymentData.city) {
      url = `${url}&city=${this.icgConfig.paymentData.city}`;
    }
    if (this.icgConfig.paymentData.state) {
      url = `${url}&state=${this.icgConfig.paymentData.state}`;
    }
    if (this.icgConfig.paymentData.zip) {
      url = `${url}&zip=${this.icgConfig.paymentData.zip}`;
    }
    if (this.icgConfig.paymentData.email) {
      url = `${url}&email=${this.icgConfig.paymentData.email}`;
    }
    if (this.icgConfig.paymentData.phone) {
      url = `${url}&phone=${this.icgConfig.paymentData.phone}`;
    }
    if (this.icgConfig.paymentData.custId) {
      url = `${url}&custId=${this.icgConfig.paymentData.custId}`;
    }
    if (this.icgConfig.paymentData.custom1) {
      url = `${url}&custom1=${this.icgConfig.paymentData.custom1}`;
    }
    if (this.icgConfig.paymentData.custom2) {
      url = `${url}&custom2=${this.icgConfig.paymentData.custom2}`;
    }
    if (this.icgConfig.paymentData.custom3) {
      url = `${url}&custom3=${this.icgConfig.paymentData.custom3}`; // TODO: Need to revisit, how to handle entire object
    }
    let css;
    if (this.icgConfig.flowType === 'OAO') {
      if (this.isACH()) {
        css = this.buildIFrameStyleOAOAch();
      } else {
        css = this.buildIFrameStyleOAOCredit();
      }
    } else {
      css = this.buildIFrameStyle();
    }

    url = `${url}&css=${css}`;
    this.trustedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  public buildIFrameStyle() {
    // Set styles according to Baseline theme guidelines
    // NOTE: This string is appended to the iframe url,
    // adding too much css here may overload the string and cause unexpected behavior.
    const themeColors: ThemeColors = this.themeService.creditUnionColors;
    const primaryColor = themeColors ? themeColors.Primary.replace('#', '%23') : null;
    const iFrameStyle = `%40import url('https%3A%2F%2Ffonts%2Egoogleapis%2Ecom%2Fcss2%3Ffamily%3DNunito%2BSans');
          body{background-color:transparent;letter-spacing:0.3px;overflow-x: hidden;}
          payment{font-family:'Nunito Sans',sans-serif!important;border:none;box-shadow:none;background-color:transparent;background-image:none;margin:0px;padding:2px;position:relative;display:inline}
          payment select, input:not([type=checkbox]):not([type=radio]){font-size:16px!important;color:%23445663;
          line-height:48px;border-radius:4px;display:inline-block;padding:0 10px 0 10px!important;height:48px!important;border:1px solid %23dde5eb}
          payment select:focus, input:focus{color:%23495057;border-color:%23087dda;outline:0;box-shadow:0 0 3px 0 rgb(0 118 222 / 50%);}
          payment label:not(.small){font-size:14px;font-weight:800;color:%23445663;}
          payment label.checkbox{float:none!important;text-shadow:none!important;font-size:14px;font-weight:400;color:%23495057!important;}
          // eslint-disable-next-line max-len
          // eslint-disable-next-line max-len
          payment input[type="submit"],payment input[type="reset"],payment input[type="button"]{color:%23ffffff!important;background-color:${primaryColor};margin:16px 0px 0px!important}
          // eslint-disable-next-line max-len
          // eslint-disable-next-line max-len
          payment input[type="submit"]:disabled,payment input[type="reset"]:disabled,payment input[type="button"]:disabled{background-color:%237e8a9a;}
          .help-block{font-size:14px!important;display:block;margin:8px 0px 16px;float:none!important;}
          i.form-control-feedback{display:none!important;}`;
    return iFrameStyle.replace(/ +(?= )/g, '');
  }

  public buildIFrameStyleOAOAch() {
    const iFrameStyle =
      // eslint-disable-next-line max-len
      'body{background:%23F5F7FA;}payment{padding:24px 56px;max-width:none;background:%23F5F8FA;border:none;box-shadow:none;color:%23515151}paymentNumber{border:1px solid %23F0ECEC;}input[type="submit"]{background-color:%232196F3;font-weight:700;font-size:14px;height:40px;border-radius:4px;border:none;text-transform:capitalize;}input[type="reset"]:after{content:"clear";}%23btnClearCard,input[type="button"],input[type="reset"]{background-color:%23FFFFFF;font-weight:700;font-size:14px;color:%232196F3;height:40px;border-radius:4px;border:none;text-transform:capitalize;}';
    return iFrameStyle.replace(/ +(?= )/g, '');
  }

  public buildIFrameStyleOAOCredit() {
    const iFrameStyle =
      // eslint-disable-next-line max-len
      '%40import+url(%27https%3a%2f%2ffonts.googleapis.com%2fcss2%3ffamily%3dNunito%2bSans%27)%3b++body%7bbackground-color%3atransparent%3bletter-spacing%3a0.3px%3boverflow-x%3a+hidden%3b%7d++payment%7bfont-family%3a%27Nunito+Sans%27%2csans-serif!important%3bborder%3anone%3bbox-shadow%3anone%3bbackground-color%3atransparent%3bbackground-image%3anone%3bmargin%3a0px%3bpadding%3a2px%3bposition%3arelative%3bdisplay%3ainline%7d+payment+select%2c+input%3anot(%5btype%3dcheckbox%5d)%3anot(%5btype%3dradio%5d)%7bfont-size%3a16px!important%3bcolor%3a%23445663%3b+line-height%3a48px%3bborder-radius%3a4px%3bdisplay%3ainline-block%3bpadding%3a0+10px+0+10px!important%3bheight%3a48px!important%3bborder%3a1px+solid+%23dde5eb%7d+payment+select%3afocus%2c+input%3afocus%7bcolor%3a%23495057%3bborder-color%3a%23087dda%3boutline%3a0%3bbox-shadow%3a0+0+3px+0+rgb(0+118+222+%2f+50%25)%3b%7d+payment+label%3anot(.small)%7bfont-size%3a14px%3bfont-weight%3a800%3bcolor%3a%23445663%3b%7d+payment+label.checkbox%7bfloat%3anone!important%3btext-shadow%3anone!important%3bfont-size%3a14px%3bfont-weight%3a400%3bcolor%3a%23495057!important%3b%7d+payment+input%5btype%3d%22submit%22%5d%2cpayment+input%5btype%3d%22reset%22%5d%2cpayment+input%5btype%3d%22button%22%5d%7bcolor%3a%23ffffff!important%3bbackground-color%3a%230b76cc%3bmargin%3a16px+0px+0px!important%7d+payment+input%5btype%3d%22submit%22%5d%3adisabled%2cpayment+input%5btype%3d%22reset%22%5d%3adisabled%2cpayment+input%5btype%3d%22button%22%5d%3adisabled%7bbackground-color%3a%237e8a9a%3b%7d+.help-block%7bfont-size%3a14px!important%3bdisplay%3ablock%3bmargin%3a8px+0px+16px%3bfloat%3anone!important%3b%7d+';
    return iFrameStyle.replace(/ +(?= )/g, '');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public receiveMessage: any = (event: any) => {
    const jsonData = event.data;
    if (typeof jsonData != 'undefined') {
      if (typeof jsonData == 'object' && jsonData.constructor.name === 'Object') {
        const source = jsonData.source;
        if (typeof source != 'undefined' && source === 'iCheck') {
          const paymentResponse = {
            token: jsonData.token,
            source: jsonData.source,
            error: jsonData.error,
            custId: jsonData.custId,
            operation: jsonData.operation,
            transactionResponse: jsonData.transactionResponse,
            cardType: jsonData.cardType,
            cardNumber: jsonData.cardNumber,
            cardExpDate: jsonData.cardExpDate,
            accountType: jsonData.accountType,
            accountNumber: jsonData.accountNumber,
            routingNumber: jsonData.routingNumber,
            bankInstitution: jsonData.bankInstitution
          };

          this.paymentDetails =
            paymentResponse && paymentResponse.transactionResponse
              ? {
                  ...paymentResponse,
                  status: paymentResponse.transactionResponse.split('|')[0],
                  trackingNumber: paymentResponse.transactionResponse.split('|')[7],
                  paymentAmount: paymentResponse.transactionResponse.split('|')[14],
                  paymentDate: paymentResponse.transactionResponse.split('|')[21],
                  bankName: paymentResponse.bankInstitution,
                  accountType: this.getAccountTypeDescription(paymentResponse?.accountType)
                }
              : {};

          // eslint-disable-next-line max-len
          this.errorMessage = `Your payment method couldn't be processed. Check the information and try again, or if you still have issues, please contact us
              ${this.icgConfig.lenderContact && this.icgConfig.lenderContact.length !== 0 ? ' at ' + this.icgConfig.lenderContact : ''}.`;
          this.transactionApproved =
            !paymentResponse.error && this.paymentDetails && this.paymentDetails.status.toString().toLowerCase() === 'approved';

          if (this.transactionApproved) {
            this.icgConfig.transactionRequest.confirmationNumber = this.paymentDetails.trackingNumber;
            const xmlInputRequest = this.prepareXMLInputRequest(this.icgConfig, this.paymentDetails.bankName);
            this.icheckGatewayService.getTransactionDetails(xmlInputRequest, this.icgConfig.apiUrl, this.icgConfig.headers).subscribe(
              (response) => {
                this.transactionSuccessful = true;
                this.transactionResponse.emit(response);
              },
              (err) => {
                this.transactionError = err;
                this.transactionSuccessful = false;
              }
            );
          }
        }
      }
    }
  };

  private prepareXMLInputRequest(icgConfig: ICheckGatewayConfig, bankName: string): string {
    let elementName = '';
    let productCode = '';
    if (this.selectedPaymentType === 'CC') {
      elementName = 'GetCreditCardTransaction';
      productCode = icgConfig.transactionRequest.productCode;
    } else {
      elementName = 'GetACHTransaction';
      productCode = icgConfig.transactionRequest.achProductCode;
    }

    const payload = `<?xml version="1.0" encoding="utf-8"?>
        <REQUEST_GROUP VersionID="1.0.0" _ID="ID1" xmlns="http://www.quantumesolutions.com/Schema/DPN">
          <REQUESTING_PARTY _Name="${icgConfig.transactionRequest.requestingPartyName}" _Identifier="${
      icgConfig.transactionRequest.edpnTenantId
    }" _TransactionIdentifier="CHKGWAY" xmlns=""/>
          <REQUESTING_PRODUCT _ProductCode="${productCode}" _ProviderCode="${icgConfig.transactionRequest.providerCode}" xmlns="" />
          <Payload xmlns="">
            <![CDATA[
              <Payload>
                <Body>
                    <${elementName} xmlns="https://trans.icheckgateway.com">
                      <ConfirmationNumber>${icgConfig.transactionRequest.confirmationNumber}</ConfirmationNumber>
                        <GatewayLiveMode>${icgConfig.transactionRequest.gatewayLiveMode}</GatewayLiveMode>
                    </${elementName}>
                </Body>
                <CustomData>
                <Email>${icgConfig.paymentData.email}</Email>
                <LoanNumber>${icgConfig.paymentData.custom1}</LoanNumber>
                <BankName>${bankName}</BankName>
                <FlowType>${this.icgConfig.flowType}</FlowType>
                ${this.getDetails()}
                </CustomData>
              </Payload>
            ]]>
          </Payload>
        </REQUEST_GROUP>`;
    return payload;
  }

  private getDetails() {
    const details = this.icgConfig.flowType && this.icgConfig.flowType === 'OAO' ? this.getsharesDetails() : this.getFeeDetails();
    return details;
  }

  public getFeeDetails() {
    let feelist = '';
    if (this.icgConfig.feeDetails && this.icgConfig.feeDetails.length > 0) {
      this.icgConfig.feeDetails.forEach((item) => {
        feelist += `<${item.feeCode} feeName='${item.feeName}' feeID='${item.feeID}'>${item.transactionAmount}</${item.feeCode}>`;
      });
    }
    return feelist ? `<FeeDetails>${feelist}</FeeDetails>` : feelist;
  }

  public getsharesDetails() {
    let shareDetails = '';
    if (this.icgConfig.paymentData && this.icgConfig.paymentData.custom3) {
      this.icgConfig.paymentData.custom3.forEach((element) => {
        // eslint-disable-next-line max-len
        shareDetails += `<Share sharesID='${element.sharesID}' type='${element.type}' applyFundsTo='${element.applyFundsTo}'>${element.amount}</Share>`;
      });
    }
    return shareDetails ? `<SharesDetails>${shareDetails}</SharesDetails>` : shareDetails;
  }

  // Changes XML to JSON
  private xmlToJson(xml) {
    // Create the return object
    let obj = {};

    if (xml.nodeType === 1) {
      // element
      // do attributes
      if (xml.attributes.length > 0) {
        obj['@attributes'] = {};
        for (let j = 0; j < xml.attributes.length; j++) {
          const attribute = xml.attributes.item(j);
          obj['@attributes'][attribute.nodeName] = attribute.nodeValue;
        }
      }
    } else if (xml.nodeType === 3) {
      // text
      obj = xml.nodeValue;
    }

    // do children
    if (xml.hasChildNodes()) {
      for (let i = 0; i < xml.childNodes.length; i++) {
        const item = xml.childNodes.item(i);
        const nodeName = item.nodeName;
        if (typeof obj[nodeName] == 'undefined') {
          obj[nodeName] = this.xmlToJson(item);
        } else {
          if (typeof obj[nodeName].push == 'undefined') {
            const old = obj[nodeName];
            obj[nodeName] = [];
            obj[nodeName].push(old);
          }
          obj[nodeName].push(this.xmlToJson(item));
        }
      }
    }
    return obj;
  }

  public selectedPayment(value) {
    this.icgSelectedPaymentType.emit(value);
    this.selectedPaymentType = value;
    this.iframeHeight = this.selectedPaymentType === 'CC' ? '1325px' : '640px';
    this.iFrameUrlBuilder();
  }

  private getIframeUrl() {
    return this.icgConfig.iframeUrls.filter((t) => t.key === this.selectedPaymentType)[0].value;
  }

  public showIframe(): boolean {
    return !!this.selectedPaymentType && !this.transactionSuccessful;
  }

  public getAccountTypeDescription(type: string): string {
    return type && this.accountTypes.get(type);
  }
}
